import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function TraditionalTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white p-12" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Header - Center aligned */}
          <div className={`text-center border-b-4 ${colors.accentBorder} pb-5 mb-6`}>
            <h1 className={`text-5xl font-bold ${colors.sectionTitle} mb-4`}>
              {personalInfo.fullName || 'YOUR NAME'}
            </h1>
            <div className="text-sm text-gray-800 space-y-2">
              <div className="flex justify-center items-center flex-wrap gap-x-4">
                {personalInfo.email && <span>✉ {personalInfo.email}</span>}
                {personalInfo.phone && <span>✆ {personalInfo.phone}</span>}
              </div>
              <div className="flex justify-center items-center flex-wrap gap-x-4">
                {personalInfo.address && <span>⚲ {personalInfo.address}</span>}
                {personalInfo.linkedin && <span>⧉ {personalInfo.linkedin}</span>}
              </div>
            </div>
          </div>

          {/* Summary */}
          {summary && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-3 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                {labels.professionalSummary}
              </h2>
              <p className="text-gray-800 text-justify leading-relaxed">{summary}</p>
            </div>
          )}

          {/* Experience */}
          {experience && experience.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-4 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                Professional Experience
              </h2>
              <div className="space-y-5">
                {experience.map((exp: any) => (
                  <div key={exp.id}>
                    <div className="flex justify-between items-baseline mb-1">
                      <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                      <span className="text-sm text-gray-700 font-semibold">{exp.startDate} - {exp.endDate || labels.present}</span>
                    </div>
                    <div className={`${colors.accentText} italic mb-2`}>
                      {exp.company}{exp.location && ` | ${exp.location}`}
                    </div>
                    <div className="text-gray-800 text-justify whitespace-pre-line leading-relaxed">
                      {exp.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {skills && skills.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-4 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                Technical Skills
              </h2>
              <div className="space-y-2">
                {skills.map((skillGroup: any, index: number) => (
                  <div key={index}>
                    <span className={`font-bold ${colors.sectionTitle}`}>{skillGroup.category}:</span>{' '}
                    <span className="text-gray-800">{skillGroup.skills?.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {education && education.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-4 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                Education
              </h2>
              {education.map((edu: any) => (
                <div key={edu.id} className="mb-3">
                  <div className="flex justify-between items-baseline">
                    <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{edu.degree}</h3>
                    <span className="text-sm text-gray-700 font-semibold">{edu.graduationDate}</span>
                  </div>
                  <div className="text-gray-800">{edu.institution}</div>
                  {edu.gpa && <div className="text-sm text-gray-700">GPA: {edu.gpa}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Projects */}
          {projects && projects.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-4 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                Projects
              </h2>
              {projects.map((project: any) => (
                <div key={project.id} className="mb-3">
                  <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{project.name}</h3>
                  <p className="text-gray-800 text-justify">{project.description}</p>
                  {project.technologies && (
                    <div className="text-sm text-gray-700 mt-1">
                      <span className="font-semibold">Technologies:</span> {project.technologies.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Certifications */}
          {certifications && certifications.length > 0 && (
            <div className="mb-6">
              <h2 className={`text-lg font-bold ${colors.sectionTitle} mb-4 uppercase tracking-wider border-b-2 ${colors.sectionBorder} pb-1`}>
                Certifications
              </h2>
              {certifications.map((cert: any) => (
                <div key={cert.id} className="mb-2">
                  <span className={`font-bold ${colors.sectionTitle}`}>{cert.name}</span>
                  <span className="text-gray-700"> - {cert.issuer}, {cert.date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  )
}
